import { FC, ReactElement, useMemo, useState } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { VipSettings, VipTier } from "services/settings/type"
import { DEFAULT_VIP_RAKEBACK } from "services/settings/vipDefaults"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

type Props = {
  vip: VipSettings | null | undefined
  onChange: (next: VipSettings | null) => void
  onSave: () => Promise<void>
  refreshSettings: () => Promise<void>
}

export const VipSettingsTab: FC<Props> = ({ vip, onChange, onSave, refreshSettings }): ReactElement => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const localVip: VipSettings = useMemo(
    () =>
      vip ?? {
        tiers: []
      },
    [vip]
  )

  const updateTier = (index: number, patch: Partial<VipTier>) => {
    const tiers = [...localVip.tiers]
    tiers[index] = { ...tiers[index], ...patch }

    onChange({
      ...localVip,
      tiers
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave()
      setHistoryKey((key) => key + 1)
    } finally {
      setSaving(false)
    }
  }

  const inputBase =
    "w-full px-2 py-1 text-xs border rounded-lg bg-white text-black border-black/10 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(169,78,206,0.35)] dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:placeholder:text-gray-500"

  return (
    <section className="flex flex-col gap-6 mt-6">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.vip.title")}</h3>
        <SettingsHistoryNote target="vip" refreshKey={historyKey} />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-white">VIP tiers (by total wager)</h4>

          {localVip.tiers.length === 0 && (
            <button
              type="button"
              onClick={() => onChange(DEFAULT_VIP_RAKEBACK)}
              className="px-4 py-2 rounded-lg border text-sm font-medium transition-all bg-white dark:bg-[#0C0C1A] text-[rgba(6,7,21,1)] dark:text-white border-black/10 dark:border-white/10 hover:bg-black/[0.03] dark:hover:bg-white/[0.06]"
            >
              Load default tiers
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-black/10 dark:border-white/10 bg-white dark:bg-transparent">
            <thead className="bg-black/[0.03] dark:bg-white/[0.04]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">ID</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">Name</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">Min total wager (USD)</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">Rakeback %</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">
                  Weekly wager rate
                  <span className="block text-[10px] text-gray-500 dark:text-gray-400">multiplier for W (e.g. 0.001)</span>
                </th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">
                  Monthly wager rate
                  <span className="block text-[10px] text-gray-500 dark:text-gray-400">multiplier for W (e.g. 0.003)</span>
                </th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">
                  Lossback rate
                  <span className="block text-[10px] text-gray-500 dark:text-gray-400">as decimal (e.g. 0.08 = 8%)</span>
                </th>
                <th className="px-3 py-2 text-left font-semibold text-[rgba(6,7,21,1)] dark:text-white">Invitation only</th>
              </tr>
            </thead>

            <tbody>
              {localVip.tiers.map((tier, index) => (
                <tr
                  key={tier.id}
                  className={clsx(
                    "border-t border-black/10 dark:border-white/10",
                    index % 2 === 0 ? "bg-white dark:bg-white/[0.02]" : "bg-black/[0.01] dark:bg-[#0C0C1A]"
                  )}
                >
                  <td className="px-3 py-2">
                    <input type="text" className={clsx(inputBase, "opacity-70 cursor-not-allowed")} value={tier.id} disabled />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className={inputBase}
                      value={tier.name}
                      onChange={(e) => updateTier(index, { name: e.target.value })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      className={inputBase}
                      value={tier.minTotalWagerUsd}
                      onChange={(e) => updateTier(index, { minTotalWagerUsd: parseFloat(e.target.value) || 0 })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      className={inputBase}
                      value={tier.rakebackPercent}
                      onChange={(e) => updateTier(index, { rakebackPercent: parseFloat(e.target.value) || 0 })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step={0.0001}
                      className={inputBase}
                      value={tier.weeklyWagerRate ?? 0}
                      onChange={(e) => updateTier(index, { weeklyWagerRate: parseFloat(e.target.value) || 0 })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step={0.0001}
                      className={inputBase}
                      value={tier.monthlyWagerRate ?? 0}
                      onChange={(e) => updateTier(index, { monthlyWagerRate: parseFloat(e.target.value) || 0 })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step={0.001}
                      className={inputBase}
                      value={tier.lossbackRate ?? 0}
                      onChange={(e) => updateTier(index, { lossbackRate: parseFloat(e.target.value) || 0 })}
                    />
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={!!tier.invitationOnly}
                        onChange={(e) => updateTier(index, { invitationOnly: e.target.checked })}
                        className="h-4 w-4 accent-[rgba(169,78,206,1)]"
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {localVip.tiers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                    No tiers configured — players get the flat rakeback percent and no wager rewards. Use &quot;Load default tiers&quot; to
                    start from the config defaults.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="vip"
        resetLabel={t("settings.vip.title")}
        onResetDone={async () => {
          await refreshSettings()
          setHistoryKey((key) => key + 1)
        }}
      />
    </section>
  )
}
